import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { emptyMemory, type UserMemory } from '../../shared/memory.ts';
import { api } from '../lib/api.ts';

/**
 * Why memory hasn't loaded yet: the server didn't answer ('unavailable'), or the session ended ('signed-out', a 401,
 * which only signing in again fixes).
 */
export type MemoryProblem = 'unavailable' | 'signed-out';

type MemoryState = {
  memory: UserMemory;
  /** Memory came from the server (or the Lab). Until then there's nothing true to show. */
  loaded: boolean;
  /** Set only before memory has loaded, when the last read failed. */
  problem: MemoryProblem | null;
  /** A read the user asked for (`retry`) is running. */
  retrying: boolean;
  refresh(): Promise<void>;
  /** Read again now, for a Try again button. A failure here doesn't slow the automatic retries. */
  retry(): Promise<void>;
  set(memory: UserMemory): void;
};

const Ctx = createContext<MemoryState | null>(null);

// Worth retrying on its own: no answer at all, a server error, a timeout or rate limit. Other 4xx won't change.
const transient = (status: number | undefined) => !status || status >= 500 || status === 408 || status === 429;

/**
 * One memory document per signed-in user, shared by the Chat and Profile tabs. With `initial` (the Lab), it serves
 * that memory and never fetches; with `stuck` as well, memory shows as never loaded, for that reason.
 */
export function MemoryProvider({
  children,
  initial,
  stuck,
}: {
  children: ReactNode;
  initial?: UserMemory;
  stuck?: MemoryProblem;
}) {
  const [memory, setMemory] = useState<UserMemory>(() => initial ?? emptyMemory());
  const [loaded, setLoaded] = useState(Boolean(initial) && !stuck);
  const [problem, setProblem] = useState<MemoryProblem | null>(stuck ?? null);
  // Automatic retries so far; each failed one doubles the wait before the next.
  const [failures, setFailures] = useState(0);
  const [stopped, setStopped] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // Reads can land out of order (a call's start, a late answer, the post-call completion), and the server's version only
  // goes up: never replace memory with an older copy.
  const apply = useCallback((next: UserMemory) => setMemory((cur) => (next.version >= cur.version ? next : cur)), []);
  // Any memory from the server counts as loaded, whichever request brought it.
  const set = useCallback(
    (next: UserMemory) => {
      apply(next);
      setLoaded(true);
      setProblem(null);
      setFailures(0);
    },
    [apply],
  );
  /** Reads memory; on failure says why and whether waiting can help. A failed read keeps what's shown. */
  const read = useCallback(async () => {
    try {
      set((await api.memory()).memory);
      return true;
    } catch (e) {
      const status = (e as { status?: number }).status;
      // Before the first good read, what's shown is an empty memory, a new user's home (no setup, no tabs), so it
      // stays not loaded and says why instead.
      setProblem(status === 401 ? 'signed-out' : 'unavailable');
      setStopped(!transient(status));
      return false;
    }
  }, [set]);
  const refresh = useCallback(async () => {
    if (initial) return;
    if (!(await read())) setFailures((n) => n + 1);
  }, [initial, read]);
  const retry = useCallback(async () => {
    if (initial) return;
    setRetrying(true);
    await read();
    setRetrying(false);
  }, [initial, read]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  useEffect(() => {
    if (loaded || stopped || !failures) return;
    const next = setTimeout(() => void refresh(), Math.min(30_000, 1000 * 2 ** failures));
    return () => clearTimeout(next);
  }, [loaded, stopped, failures, refresh]);
  return (
    <Ctx.Provider value={{ memory, loaded, problem: loaded ? null : problem, retrying, refresh, retry, set }}>
      {children}
    </Ctx.Provider>
  );
}

export function useMemory(): MemoryState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useMemory outside MemoryProvider');
  return ctx;
}
